/**
 * Represents a calendar event.
 */
class Event {
    /**
     * Creates a new Event instance.
     * @param {string} id - Unique identifier for the event.
     * @param {string} title - Title or name of the event.
     * @param {Date|string} start - Start date and time of the event (UTC format preferred).
     * @param {Date|string} end - End date and time of the event (UTC format preferred).
     * @param {boolean} allDay - Indicates if the event spans the entire day.
     * @param {string} type - The type of the event. Possible values: 'reminder', 'context', 'private'.
     * @param {boolean} [notified=false] - Flag indicating if the user has been notified (only for reminders).
     * @param {string} [contextText=""] - Additional context text (only for context events).
     * @param {string} [description=""] - Optional description or details about the event.
     * @param {Object|null} [repeat=null] - Optional repeat rule (e.g., { interval: 1, unit: 'week', until: '2025-12-31T23:59:59.000Z' }).
     */
    constructor(id, title, start, end, allDay = false, type, notified = false, contextText = '', description = '', repeat = null) {
        this.id = id; // Event ID (e.g., UUID or unique string)
        this.title = title; // Event title
        this.start = new Date(start); // Start time of the event
        this.end = new Date(end); // End time of the event
        this.allDay = allDay; // Is the event an all-day event?
        this.type = type; // Type of event: reminder, context, or private
        this.contextText = contextText; // Additional context for the event
        this.notified = notified; // Has the user been notified for the reminder?
        this.description = description; // Optional event description
        this.repeat = repeat; // Repeating of an event
    };

    /**
     * Checks if the event (or any repeated instance) is ongoing at the given time.
     * @param {Date} date - The current date/time to check against.
     * @returns {boolean}
     */
    isOngoing(date) {
        const now = new Date(date);
        if (!this.repeat) {
            return now >= this.start && now <= this.end;
        }
        const { interval, unit, until } = this.repeat;
        const untilDate = until ? new Date(until) : new Date(now.getFullYear() + 1, now.getMonth());
        if (now > untilDate) return false;
        const diff = {
            day: Math.floor((now - this.start) / (1000 * 60 * 60 * 24)),
            week: Math.floor((now - this.start) / (1000 * 60 * 60 * 24 * 7)),
            month: (now.getFullYear() - this.start.getFullYear()) * 12 + (now.getMonth() - this.start.getMonth())
        };
        const periods = Math.floor(diff[unit] / interval);
        if (periods < 0) return false;
        const instanceStart = new Date(this.start);
        const instanceEnd = new Date(this.end);
        switch (unit) {
            case 'day':
                instanceStart.setDate(instanceStart.getDate() + periods * interval);
                instanceEnd.setDate(instanceEnd.getDate() + periods * interval);
                break;
            case 'week':
                instanceStart.setDate(instanceStart.getDate() + periods * interval * 7);
                instanceEnd.setDate(instanceEnd.getDate() + periods * interval * 7);
                break;
            case 'month':
                instanceStart.setMonth(instanceStart.getMonth() + periods * interval);
                instanceEnd.setMonth(instanceEnd.getMonth() + periods * interval);
                break;
        }
        return now >= instanceStart && now <= instanceEnd;
    };
    /**
     * Marks a reminder event as notified.
     * Throws an error if the event type is not 'reminder'.
     */
    markAsNotified() {
        if (this.type !== 'reminder') {
        throw new Error("Only 'reminder' events can be marked as notified.");
        }
        this.notified = true;
    };
    /**
     * Returns a formatted string representation of the event.
     * Includes type-specific fields.
     * @returns {string} The event as a string.
     */
    toString() {
        let base = `${this.title} (from ${this.start.toLocaleString()} to ${this.end.toLocaleString()})`;
        if (this.type === 'reminder') {
          base += ` - Notified: ${this.notified}`;
        } else if (this.type === 'context') {
          base += ` - Context: ${this.contextText}`;
        } else if (this.type === 'private') {
          base += ` - Private event`;
        }
        if (this.repeat) {
          const plural = this.repeat.interval > 1 ? 's' : '';
          base += ` - Repeats every ${this.repeat.interval} ${this.repeat.unit}${plural}`;
          if (this.repeat.until) {
            base += ` until ${new Date(this.repeat.until).toLocaleDateString()}`;
          }
        }
        return base;
    };
};

export default Event;  